import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import useFontFaceObserver from '@sorare/use-font-face-observer';
import { StarBall } from 'atoms/icons/StarBall';
import { Horizontal } from 'atoms/layout/flex';
import { Title3 } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import { LeaguesBlock } from 'components/landing/LeaguesBlock';
import { PageHeader } from 'components/navigation/PageHeader';
import { MLB } from 'constants/__generated__/routes';
import { NBA_LANDING, useDefaultSportPages } from 'constants/routes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import useReferrer from 'contexts/queryString/useReferrer';
import { Level, useSnackNotificationContext } from 'contexts/snackNotification';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import useLifecycle from 'hooks/useLifecycle';
import useLocalStorage from 'hooks/useLocalStorage';
import { useLocalVariants } from 'hooks/useLocalVariants';
import { useEvents } from 'lib/events/useEvents';
import { glossary } from 'lib/glossary';

import { PlayNowSportsButtons } from '../HeroBlock/PlayNowSportsButtons';
import { NewOtherSports } from '../NewOtherSports';
import { Community } from './Community';
import { Experience } from './Experience';
import { Hero } from './Hero';
import { Prizes } from './Prizes';

const AB_TEST_LOCALSTORAGE_KEY = 'REDIRECTED_NBA_LANDING';

export const LandingMultiSport = () => {
  const { currentUser } = useCurrentUserContext();
  const fontStatus = useFontFaceObserver(
    [{ family: 'DrukWide-Super', weight: 'bold' }],
    { timeout: 1000 }
  );
  const { referrer, invalidReferrer, removeReferrer } = useReferrer();
  const { showNotification } = useSnackNotificationContext();
  const track = useEvents();
  const location = useLocation();
  const { lifecycle } = useLifecycle();
  const isTabletAndAbove = useIsTabletAndAbove();

  const lastVisitedSport = lifecycle?.lastVisitedSport;
  const defaultSportPages = useDefaultSportPages();
  const [hasRedirected, setHasRedirected] = useLocalStorage(
    AB_TEST_LOCALSTORAGE_KEY,
    false
  );
  const navigate = useNavigate();
  const {
    currentLocation: { countryCode },
  } = useConfigContext();
  const useUsSportsRedirection = useLocalVariants('useUsSportsRedirection');

  useEffect(() => {
    if (invalidReferrer) {
      showNotification(
        'unknownReferrer',
        { referrer, strong: Bold },
        {
          level: Level.ERROR,
          autoHideDuration: null,
          onClosed: removeReferrer,
        }
      );
    }
  }, [showNotification, invalidReferrer, referrer, removeReferrer]);

  let redirect: typeof NBA_LANDING | typeof MLB | null = null;
  if (
    !currentUser &&
    !hasRedirected &&
    countryCode &&
    ['US'].includes(countryCode)
  ) {
    if (useUsSportsRedirection === 'withUsSportsRedirectionNBA') {
      redirect = NBA_LANDING;
    }
    if (useUsSportsRedirection === 'withUsSportsRedirectionMLB') {
      redirect = MLB;
    }
  }

  useEffect(() => {
    if (redirect) {
      setHasRedirected(true);
      navigate(redirect, { replace: true });
    }
  }, [redirect, navigate, setHasRedirected]);

  useEffect(() => {
    if (!currentUser) {
      track('View Homepage Disconnected', {
        useUsSportsRedirection,
        countryCode,
      });
    }
  }, [currentUser, track, useUsSportsRedirection, countryCode]);

  if (lastVisitedSport) {
    return (
      <Navigate
        to={`${defaultSportPages[lastVisitedSport]}${location.search}`}
        replace
        state={location.state}
      />
    );
  }

  if (fontStatus === 'initial') return null; // allow rendering without the right font after the timeout

  if (currentUser) {
    return (
      <>
        <PageHeader>
          <Horizontal gap={2}>
            <StarBall />
            <Title3>
              <FormattedMessage {...glossary.chooseYourSport} />
            </Title3>
          </Horizontal>
        </PageHeader>
        {isTabletAndAbove ? (
          <NewOtherSports title=" " />
        ) : (
          <PlayNowSportsButtons />
        )}
      </>
    );
  }

  return (
    <>
      <Hero />
      <LeaguesBlock />
      <Experience />
      <Prizes />
      <Community />
      <NewOtherSports />
    </>
  );
};
